/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Hidden, SelectField, SelectOption, Typography, Typography2, breakpoints } from '@coursera/cds-core';
import { ChevronNextIcon } from '@coursera/cds-icons';

import type {
  EntWebsiteBlockCaseStudyQuery,
  EntWebsiteCaseStudyFragment,
} from 'bundles/ent-website/__generated__/contentfulTypes';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import BlockLayout, { topImageOnPhone } from 'bundles/ent-website/contentful/core/BlockLayout';
import { TestimonialModule } from 'bundles/ent-website/contentful/modules/TestimonialModule';
import { entWebsiteBlockCaseStudyQuery } from 'bundles/ent-website/queries/EntWebsiteCaseStudy';
import type { BlockModuleInnerProps } from 'bundles/ent-website/utils/withBlockQuery';
import withBlockQuery from 'bundles/ent-website/utils/withBlockQuery';

import _t from 'i18n!nls/ent-website';

type Props = BlockModuleInnerProps<EntWebsiteCaseStudyFragment>;

export function CaseStudyModule({ content, assembly }: Props) {
  const teasers = content.teasersCollection?.items ?? [];
  const styles = {
    menu: css`
      /* stylelint-disable-line no-empty-source */
    `,

    headline: css`
      font-size: 4rem !important;
      line-height: 100% !important;
      letter-spacing: -0.04em;

      ${breakpoints.down('xs')} {
        font-size: 2.5rem !important;
      }
    `,

    menuItems: css`
      list-style-type: none;
      margin-top: var(--cds-spacing-600);
      padding: 0;

      li button {
        width: 100%;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        height: 104px;
        padding: 0 var(--cds-spacing-400);
        border: 1px solid var(--cds-color-grey-50);
        border-radius: 16px;
        background: var(--cds-color-white-0);
        margin-bottom: var(--cds-spacing-200);
        cursor: pointer;
        color: var(--cds-color-neutral-primary-weak);

        p {
          font-size: 1.5rem !important;
        }

        &:hover {
          border: 1px solid var(--cds-color-blue-700);
        }

        transition: border 0.2s ease-in-out;

        svg {
          transition: transform 0.2s ease-in-out;
          width: 12px;
        }
      }

      li.CaseStudyModule-selected button {
        border: 1px solid var(--cds-color-blue-700);
        color: var(--cds-color-grey-975);

        svg {
          transform: rotate(180deg);
        }
      }
    `,

    teaser: css`
      ${breakpoints.down('xs')} {
        margin-top: var(--cds-spacing-400);
      }
    `,

    teaserHeadline: css`
      font-size: 3rem !important;
      font-weight: 600;
      line-height: 100%;
      letter-spacing: -0.04em;
      margin-bottom: var(--cds-spacing-400);

      ${breakpoints.down('xs')} {
        font-size: 2.25rem !important;
      }
    `,

    teaserSectionTag: css`
      font-size: 1.25rem !important;
      font-weight: 600;
      line-height: 1.75rem !important;
      letter-spacing: -0.1px;
      color: var(--cds-color-grey-600);
      text-transform: uppercase;
      margin-bottom: var(--cds-spacing-400);
    `,

    teaserText: css`
      font-size: 1.75rem !important;
      font-weight: 400;
      line-height: 150% !important;
      letter-spacing: -0.03rem;
      margin-bottom: var(--cds-spacing-400);
      padding-left: var(--cds-spacing-300);
      position: relative;

      /* the vertical bar */
      &::before {
        content: '';
        border: 1px solid var(--cds-color-grey-300);
        display: inline-block;
        position: relative;
        left: -24px;
        top: -12px;
        height: 12px;
      }

      ${breakpoints.down('xs')} {
        font-size: 1rem !important;
        padding-left: 0;

        &::before {
          display: none;
        }
      }
    `,

    dropdownMenu: css`
      margin-top: var(--cds-spacing-200);

      label {
        display: none;
      }
    `,
  };

  const [selectedIndex, setSelectedIndex] = React.useState(0);

  const selectedTeaser = teasers[selectedIndex];

  const handleMenuItemClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const menuItemIndex = (e.currentTarget as HTMLButtonElement).dataset.menuitemIndex;
    if (!menuItemIndex) {
      return;
    }
    const idx = parseInt(menuItemIndex, 10);
    if (!isNaN(idx)) {
      setSelectedIndex(idx);
    }
  };

  const handleDropdownChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    const value = e.target.value as string;

    const idx = parseInt(value, 10);
    if (!isNaN(idx)) {
      setSelectedIndex(idx);
    }
  };

  const menuPanel = () => (
    <div css={styles.menu}>
      <Typography variant="d1semibold" css={styles.headline}>
        {content.headline}
      </Typography>
      <Hidden smUp>
        <SelectField
          value={selectedIndex}
          label={content.headline ?? ''}
          css={styles.dropdownMenu}
          fullWidth
          onChange={handleDropdownChange}
        >
          {teasers.map((teaser, idx) => (
            <SelectOption value={idx} key={teaser?.slug}>
              {teaser?.orgName}
            </SelectOption>
          ))}
        </SelectField>
      </Hidden>
      <Hidden xsDown>
        <ul role="menu" css={styles.menuItems} tabIndex={-1}>
          {teasers.map((teaser, idx) => (
            <li
              role="presentation"
              key={teaser?.slug}
              className={idx === selectedIndex ? 'CaseStudyModule-selected' : undefined}
            >
              <button
                type="button"
                role="menuitem"
                aria-haspopup="true"
                onClick={handleMenuItemClick}
                data-menuitem-index={idx}
              >
                <Typography2 component="p" color="inherit">
                  {' '}
                  {teaser?.orgName}
                </Typography2>
                <ChevronNextIcon size="small" color="interactive" />
              </button>
            </li>
          ))}
        </ul>
      </Hidden>
    </div>
  );

  const contentPanel = () => (
    <div css={styles.teaser}>
      <Typography variant="d2semibold" css={styles.teaserHeadline}>
        {selectedTeaser?.headline}
      </Typography>
      <Typography2 component="p" css={styles.teaserSectionTag}>
        {_t('Problem')}
      </Typography2>
      <Typography2 component="p" css={styles.teaserText}>
        {selectedTeaser?.problem}
      </Typography2>
      <Typography2 component="p" css={styles.teaserSectionTag}>
        {_t('Solution')}
      </Typography2>
      <Typography2 component="p" css={styles.teaserText}>
        {selectedTeaser?.solution}
      </Typography2>

      {selectedTeaser?.testimonial && (
        <TestimonialModule
          content={selectedTeaser.testimonial}
          assembly={{
            ...assembly,
            layout: 'no-image',
            visibility: {
              headline: true,
              testimonial: true,
              authorOrganization: true,
              author: true,
              authorTitle: true,
            },
          }}
        />
      )}
    </div>
  );

  return (
    <BlockLayout
      assembly={{
        ...topImageOnPhone(assembly),
        layout: 'image30/text70',
      }}
      renderContent={contentPanel}
      renderImage={menuPanel}
    />
  );
}

// @ts-expect-error TSMIGRATION-5.0 TS2344
export const BlockCaseStudyModule = withBlockQuery<EntWebsiteCaseStudyFragment, EntWebsiteBlockCaseStudyQuery>(
  entWebsiteBlockCaseStudyQuery
)(CaseStudyModule);
